import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi, RefundPolicySettings, RefundReason, SellerFeeFundingSource } from '../services/api';
import { transactionStatusDisplay } from '../utils/transactionDisplay';

const fmt = (n: number) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtKr = (n: number) => `${fmt(n)} kr`;
const defaultRefundPolicySettings: RefundPolicySettings = {
  conflictStep1SellerFeeTreatment: 'retain',
  conflictStep1SellerFeeFundingSource: 'etappio_balance',
  withdrawalRightSellerFeeFundingSource: 'etappio_balance',
  conflictLevel2SellerFeeTreatment: 'retain',
  conflictLevel2SellerFeeFundingSource: 'etappio_balance'
};

const refundPolicySettings = (detail: any): RefundPolicySettings => ({
  ...defaultRefundPolicySettings,
  ...(detail?.refundPolicySettings ?? {})
});

const sellerFeeTreatmentForReason = (
  settings: RefundPolicySettings,
  reason: RefundReason
): 'retain' | 'refund' => {
  if (reason === 'withdrawal_right') return 'refund';
  if (reason === 'conflict_step_1') return settings.conflictStep1SellerFeeTreatment;
  if (reason === 'conflict_level_2') return settings.conflictLevel2SellerFeeTreatment;
  return 'retain';
};

const fundingSourceForReason = (
  settings: RefundPolicySettings,
  reason: RefundReason | '',
  sellerFeeTreatment: 'retain' | 'refund'
): SellerFeeFundingSource => {
  if (reason === 'withdrawal_right') return settings.withdrawalRightSellerFeeFundingSource;
  if (reason === 'conflict_step_1' && sellerFeeTreatment === 'refund') {
    return settings.conflictStep1SellerFeeFundingSource;
  }
  if (reason === 'conflict_level_2' && sellerFeeTreatment === 'refund') {
    return settings.conflictLevel2SellerFeeFundingSource;
  }
  return 'etappio_balance';
};

const refundableForPolicy = (
  detail: any,
  reason: RefundReason | '',
  sellerFeeTreatment: 'retain' | 'refund'
) => {
  const refundability = detail?.refundability;
  const base = refundability?.refundableAmountOre ?? detail?.transaction?.refundableAmountOre ?? 0;
  if (!reason) return 0;
  if (reason === 'withdrawal_right') {
    return refundability?.refundableAmountWithdrawalRightOre ?? base;
  }
  if (reason === 'conflict_level_2') {
    return sellerFeeTreatment === 'refund'
      ? refundability?.refundableAmountConflictLevel2RefundSellerFeeOre ??
          refundability?.refundableAmountIncludingSellerFeeOre ??
          base
      : refundability?.refundableAmountConflictLevel2RetainSellerFeeOre ?? base;
  }
  if (reason === 'conflict_step_1') {
    return sellerFeeTreatment === 'refund'
      ? refundability?.refundableAmountConflictStep1RefundSellerFeeOre ??
          refundability?.refundableAmountIncludingSellerFeeOre ??
          base
      : refundability?.refundableAmountConflictStep1RetainSellerFeeOre ??
          refundability?.refundableAmountConflictStep1Ore ??
          base;
  }
  return base;
};

function AccountingEntriesTable({ entries }: { entries: any[] }) {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table className="table table-sm mb-0">
        <thead className="table-light">
          <tr>
            <th>{t('adminTransactionDetail.columns.verification')}</th>
            <th>{t('adminTransactionDetail.columns.type')}</th>
            <th>{t('adminTransactionDetail.columns.debit')}</th>
            <th>{t('adminTransactionDetail.columns.credit')}</th>
            <th className="text-end">{t('adminTransactionDetail.columns.amount')}</th>
            <th className="text-end">{t('adminTransactionDetail.columns.vat')}</th>
            <th>{t('adminTransactionDetail.columns.text')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                {t('adminTransactionDetail.emptyStates.noAccountingEntries')}
              </td>
            </tr>
          )}
          {entries.map((entry: any) => (
            <tr key={entry.id}>
              <td className="font-monospace small">{entry.verificationNumber}</td>
              <td><span className="badge bg-light text-dark border">{entry.entryType}</span></td>
              <td className="small">
                <span className="font-monospace">{entry.debitAccount}</span>
                <span className="text-muted d-block" style={{ fontSize: 10 }}>{entry.debitAccountName}</span>
              </td>
              <td className="small">
                <span className="font-monospace">{entry.creditAccount}</span>
                <span className="text-muted d-block" style={{ fontSize: 10 }}>{entry.creditAccountName}</span>
              </td>
              <td className="text-end fw-semibold">{fmt(entry.amountSek)}</td>
              <td className="text-end text-muted">{entry.vatSek != null ? fmt(entry.vatSek) : ''}</td>
              <td className="small">{entry.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminTransactionDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundRequestKey, setRefundRequestKey] = useState<string | null>(null);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundConfirmed, setRefundConfirmed] = useState(false);
  const [refundReason, setRefundReason] = useState<RefundReason | ''>('');
  const [sellerFeeTreatment, setSellerFeeTreatment] = useState<'retain' | 'refund'>('retain');
  const [sellerFeeFundingSource, setSellerFeeFundingSource] =
    useState<SellerFeeFundingSource>('etappio_balance');
  const [refundNote, setRefundNote] = useState('');
  const [refundMessage, setRefundMessage] = useState<string | null>(null);
  const [refundError, setRefundError] = useState<string | null>(null);

  const loadTransaction = async () => {
    if (!id) return;
    const detail = await adminApi.getAdminTransactionDetail(id);
    setData(detail);
    setRefundAmount('');
    setRefundReason('');
    setSellerFeeTreatment('retain');
    setSellerFeeFundingSource('etappio_balance');
    setRefundNote('');
    setRefundConfirmed(false);
  };

  useEffect(() => {
    if (!id) return;
    loadTransaction()
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        setError(err?.response?.data?.error || t('adminTransactionDetail.errors.load'));
        setLoading(false);
      });
    // The transaction id is the complete resource identity for this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitRefund = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!id || refundSubmitting) return;
    if (!refundConfirmed) {
      setRefundError(t('adminTransactionDetail.errors.confirmRequired'));
      return;
    }
    if (!refundReason) {
      setRefundError(t('adminTransactionDetail.errors.reasonRequired'));
      return;
    }
    const amountOre = Math.round(Number(refundAmount.replace(',', '.')) * 100);
    const refundableAmountOre = refundableForPolicy(
      data,
      refundReason,
      sellerFeeTreatment
    );
    if (!Number.isInteger(amountOre) || amountOre <= 0) {
      setRefundError(t('adminTransactionDetail.errors.amountPositive'));
      return;
    }
    if (amountOre > refundableAmountOre) {
      setRefundError(t('adminTransactionDetail.errors.amountExceeds', { amount: fmtKr(refundableAmountOre / 100) }));
      return;
    }
    if (
      (refundReason === 'withdrawal_right' || refundReason === 'conflict_level_2') &&
      amountOre !== refundableAmountOre
    ) {
      setRefundError(t('adminTransactionDetail.errors.fullAmountRequired'));
      return;
    }
    if (refundReason === 'conflict_step_1' && !refundNote.trim()) {
      setRefundError(t('adminTransactionDetail.errors.describeAgreement'));
      return;
    }
    const idempotencyKey = refundRequestKey ?? crypto.randomUUID();
    setRefundRequestKey(idempotencyKey);
    setRefundSubmitting(true);
    setRefundError(null);
    setRefundMessage(null);
    try {
      await adminApi.refundAdminTransaction(id, {
        amountOre,
        idempotencyKey,
        refundReason,
        sellerFeeTreatment,
        ...(refundReason === 'withdrawal_right' ||
        (['conflict_step_1', 'conflict_level_2'].includes(refundReason) &&
          sellerFeeTreatment === 'refund')
          ? { sellerFeeFundingSource }
          : {}),
        note: refundNote.trim() || undefined
      });
    } catch (err: any) {
      const response = err?.response?.data;
      const message = Array.isArray(response?.message) ? response.message.join(' ') : response?.message || response?.error;
      setRefundError(message || t('adminTransactionDetail.errors.adyenInit'));
      if ((err?.response?.status ?? 500) < 500) {
        setRefundRequestKey(null);
      }
      setRefundSubmitting(false);
      return;
    }
    setRefundMessage(t('adminTransactionDetail.messages.refundSubmitted'));
    setRefundRequestKey(null);
    try {
      await loadTransaction();
    } catch {
      setRefundError(t('adminTransactionDetail.errors.viewNotUpdated'));
    } finally {
      setRefundSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger m-4">{error || t('adminTransactionDetail.errors.notFound')}</div>
      </DashboardLayout>
    );
  }

  const {
    transaction: tx,
    entries,
    project,
    contract,
    milestone,
    company,
    customer,
    refunds = [],
    refundability,
    buyerPrincipalLiability
  } = data;
  const isOutgoing = tx.direction === 'outgoing';
  const paymentHasGoneThrough = !['pending', 'failed', 'cancelled', 'expired'].includes(tx.status);
  const etappioEntries = entries.filter((entry: any) => entry.ledgerScope === 'etappio');
  const sellerEntries = entries.filter((entry: any) => entry.ledgerScope === 'seller');
  const statusDisplay = transactionStatusDisplay(tx.status);
  const etappioTransactionFeeSek = tx.etappioTransactionFeeSek ?? tx.transactionFeeSek ?? 0;
  const customerEtappioTransactionFeeSek = tx.customerEtappioTransactionFeeSek ?? tx.customerTransactionFeeSek ?? 0;
  const companyEtappioTransactionFeeSek = tx.companyEtappioTransactionFeeSek ?? tx.companyTransactionFeeSek ?? 0;
  const paymentMethodFeeSek = tx.paymentMethodFeeSek ?? 0;
  const minimumFeeAdjustmentSek = tx.minimumFeeAdjustmentSek ?? 0;
  const customerMinimumFeeAdjustmentSek = tx.customerMinimumFeeAdjustmentSek ?? minimumFeeAdjustmentSek;
  const companyMinimumFeeAdjustmentSek = tx.companyMinimumFeeAdjustmentSek ?? 0;
  const adyenFeeSek = tx.adyenFeeSek ?? 0;
  const etappioCostSek = tx.etappioCostSek ?? 0;
  const transactionFeeBaseSek = tx.transactionFeeBaseSek ?? tx.projectValueSek;
  const policyDefaults = refundPolicySettings(data);
  const selectedRefundableAmountOre = refundableForPolicy(
    data,
    refundReason,
    sellerFeeTreatment
  );
  const canConfigureRefund = refundability?.canRefund ?? tx.canRefund;
  const fundingAccountText =
    sellerFeeFundingSource === 'seller_balance'
      ? t('adminTransactionDetail.fundingText.seller')
      : t('adminTransactionDetail.fundingText.etappio');

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminTransactionDetail.labels.transaction')} {tx.transactionReference}</h5>
          </div>
          <Breadcrumb
            items={[
              { label: t('adminTransactionDetail.breadcrumb.home'), path: '/admin' },
              { label: t('adminTransactionDetail.breadcrumb.transactions'), path: '/admin/transactions' },
              { label: tx.transactionReference }
            ]}
          />
        </div>
      </div>

      {buyerPrincipalLiability && (
        <Card className="mb-4" title={t('adminTransactionDetail.labels.debtToBuyer')}>
          <div className="row g-3 small mb-3">
            <div className="col-md-4">
              <div className="text-muted">{t('adminTransactionDetail.labels.buyerCounterparty')}</div>
              <div className="fw-semibold">{buyerPrincipalLiability.buyer?.fullName || buyerPrincipalLiability.buyerUserId}</div>
              {buyerPrincipalLiability.buyer?.email && <div className="text-muted">{buyerPrincipalLiability.buyer.email}</div>}
            </div>
            <div className="col-md-4">
              <div className="text-muted">{t('adminTransactionDetail.labels.originalCustomerPrincipal')}</div>
              <div className="fw-semibold">{fmtKr(buyerPrincipalLiability.originalPrincipalOre / 100)}</div>
              <div className="font-monospace text-muted">{buyerPrincipalLiability.originalPaymentReference}</div>
            </div>
            <div className="col-md-4">
              <div className="text-muted">{t('adminTransactionDetail.labels.remainingDebt')}</div>
              <div className={`fw-bold ${buyerPrincipalLiability.status === 'settled' ? 'text-success' : 'text-warning'}`}>
                {fmtKr(buyerPrincipalLiability.remainingPrincipalOre / 100)}
              </div>
              <span className={`badge ${buyerPrincipalLiability.status === 'settled' ? 'bg-success' : 'bg-warning text-dark'}`}>
                {buyerPrincipalLiability.status === 'settled' ? t('adminTransactionDetail.status.settled') : t('adminTransactionDetail.status.open')}
              </span>
            </div>
          </div>
          <div className="alert alert-light border py-2 small">
            {t('adminTransactionDetail.info.principalLiabilityExplanation')}
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>{t('adminTransactionDetail.columns.disposition')}</th>
                  <th>{t('adminTransactionDetail.columns.source')}</th>
                  <th>{t('adminTransactionDetail.columns.date')}</th>
                  <th className="text-end">{t('adminTransactionDetail.columns.change')}</th>
                </tr>
              </thead>
              <tbody>
                {(buyerPrincipalLiability.dispositions || []).map((disposition: any) => {
                  const labels: Record<string, string> = {
                    seller_fee: t('adminTransactionDetail.dispositionKinds.sellerFee'),
                    milestone_completion: t('adminTransactionDetail.dispositionKinds.milestoneCompletion'),
                    refund: t('adminTransactionDetail.dispositionKinds.refund'),
                    other: t('adminTransactionDetail.dispositionKinds.other')
                  };
                  const sign = disposition.direction === 'increase' ? '+' : '−';
                  return (
                    <tr key={disposition.id}>
                      <td>
                        <span className="fw-semibold">{labels[disposition.kind] || disposition.kind}</span>
                        <div className="text-muted">{disposition.description}</div>
                      </td>
                      <td className="font-monospace small">{disposition.sourceReference || disposition.sourceId}</td>
                      <td>{new Date(disposition.occurredAt).toLocaleString('sv-SE')}</td>
                      <td className={`text-end fw-semibold ${disposition.direction === 'increase' ? 'text-warning' : ''}`}>
                        {sign}{fmtKr(disposition.amountOre / 100)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="row g-4 mb-4">
        <div className="col-md-8">
          <Card title={t('adminTransactionDetail.labels.transaction')}>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className={`badge ${statusDisplay.badgeClass}`}>{statusDisplay.label}</span>
              <span className={`badge ${isOutgoing ? 'bg-warning text-dark' : 'bg-info'}`}>
                {isOutgoing ? t('adminTransactionDetail.status.outgoing') : t('adminTransactionDetail.status.incoming')}
              </span>
              <span className="badge bg-light text-dark border">{tx.paymentMethodLabel}</span>
              {tx.adyenReference && (
                <span className="badge bg-secondary font-monospace" title={t('adminTransactionDetail.labels.adyenPspReference')}>{tx.adyenReference}</span>
              )}
              {isOutgoing && tx.isFirstPayout && <span className="badge bg-secondary">{t('adminTransactionDetail.labels.firstPayout')}</span>}
            </div>

            <div className="row g-3 small">
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.paymentBaseAmount')}</div>
                <div className="fw-semibold">{fmtKr(tx.projectValueSek)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">
                  {isOutgoing ? t('adminTransactionDetail.labels.paidOut') : paymentHasGoneThrough ? t('adminTransactionDetail.labels.totalPaid') : t('adminTransactionDetail.labels.requestedAmount')}
                </div>
                <div className={`fw-bold ${isOutgoing ? 'text-warning' : 'text-success'}`}>
                  {isOutgoing ? '−' : ''}{fmtKr(tx.totalPaidSek)}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.etappioTransactionFee', { pct: tx.feePct })}</div>
                <div>{fmtKr(etappioTransactionFeeSek)}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {t('adminTransactionDetail.labels.feeSplitCustomerCompanyEtappio', { customer: fmtKr(customerEtappioTransactionFeeSek), company: fmtKr(companyEtappioTransactionFeeSek) })}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.etappioProjectFee')}</div>
                <div>{fmtKr(tx.projectFeeSek)}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {t('adminTransactionDetail.labels.feeSplitCustomerCompany', { customer: fmtKr(tx.customerProjectFeeSek ?? 0), company: fmtKr(tx.companyProjectFeeSek ?? 0) })}
                </div>
              </div>
              {minimumFeeAdjustmentSek > 0 && (
                <div className="col-md-6">
                  <div className="text-muted">{t('adminTransactionDetail.labels.minimumFeeAdjustment')}</div>
                  <div>{fmtKr(minimumFeeAdjustmentSek)}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {t('adminTransactionDetail.labels.feeSplitCustomerCompany', { customer: fmtKr(customerMinimumFeeAdjustmentSek), company: fmtKr(companyMinimumFeeAdjustmentSek) })}
                  </div>
                </div>
              )}
              {paymentMethodFeeSek > 0 && (
                <div className="col-md-6">
                  <div className="text-muted">{t('adminTransactionDetail.labels.paymentMethodFee', { method: tx.paymentMethodLabel })}</div>
                  <div>{fmtKr(paymentMethodFeeSek)}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {t('adminTransactionDetail.labels.separateFromEtappioFee')}
                  </div>
                </div>
              )}
              {adyenFeeSek > 0 && (
                <div className="col-md-6">
                  <div className="text-muted">{t('adminTransactionDetail.labels.adyenFee')}</div>
                  <div>{fmtKr(adyenFeeSek)}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {t('adminTransactionDetail.labels.supplierCostNote')}
                  </div>
                </div>
              )}
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.totalFeeCustomer')}</div>
                <div>{fmtKr(tx.customerFeeSek)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.totalFeeCompany')}</div>
                <div>{fmtKr(tx.companyFeeSek)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.etappioGrossRevenue')}</div>
                <div className="text-primary fw-semibold">{fmtKr(tx.etappioRevenueSek || 0)}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {t('adminTransactionDetail.labels.bookedFeesBeforeCosts')}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.calculatedInternalMargin')}</div>
                <div className="text-success fw-semibold">
                  {fmtKr(tx.etappioMarginSek || 0)} ({tx.etappioMarginPct || 0}%)
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {t('adminTransactionDetail.labels.marginBreakdown', { revenue: fmtKr(tx.etappioRevenueSek || 0), cost: fmtKr(etappioCostSek) })}
                </div>
              </div>
              <div className="col-12">
                <div className="alert alert-info py-2 mb-0 small">
                  <strong>{t('adminTransactionDetail.labels.feeExplanationTitle')}</strong> {t('adminTransactionDetail.labels.feeExplanationBody')}
                  {transactionFeeBaseSek !== tx.projectValueSek && (
                    <>
                      {' '}
                      {t('adminTransactionDetail.labels.feeBaseNote', { contractValue: fmtKr(transactionFeeBaseSek), baseAmount: fmtKr(tx.projectValueSek) })}
                    </>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.refunded')}</div>
                <div className={tx.refundedAmountSek > 0 ? 'text-warning fw-semibold' : ''}>{fmtKr(tx.refundedAmountSek || 0)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.heldAmount')}</div>
                <div className={(tx.heldAmountSek || 0) > 0 ? 'text-info fw-semibold' : 'text-muted'}>{fmtKr(tx.heldAmountSek || 0)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.chargebackDispute')}</div>
                <div className={tx.disputedAmountSek > 0 ? 'text-danger fw-semibold' : ''}>{fmtKr(tx.disputedAmountSek || 0)}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.payoutStatus')}</div>
                <div className={tx.payoutReady ? 'text-success fw-semibold' : 'text-muted'}>
                  {tx.payoutReady ? t('adminTransactionDetail.status.payoutReady') : t('adminTransactionDetail.status.payoutNotReady')}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{t('adminTransactionDetail.labels.created')}</div>
                <div>{new Date(tx.createdAt).toLocaleString('sv-SE')}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">{isOutgoing ? t('adminTransactionDetail.labels.recipient') : t('adminTransactionDetail.labels.paidBy')}</div>
                <div className="fw-semibold">{isOutgoing ? tx.recipientName || '—' : tx.paidByName}</div>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-md-4">
          <Card title={t('adminTransactionDetail.labels.linkedRecords')}>
            <ul className="list-unstyled mb-0 small">
              {project && (
                <li className="mb-2">
                  <i className="feather-folder me-2 text-muted"></i>
                  <Link to={`/projects/${project.id}`}>{t('adminTransactionDetail.labels.project', { number: project.projectNumber })}</Link>
                  <div className="text-muted ps-4">{project.description}</div>
                </li>
              )}
              {contract && (
                <li className="mb-2">
                  <i className="feather-file-text me-2 text-muted"></i>
                  {t('adminTransactionDetail.labels.contract', { number: contract.contractNumber })}
                  <div className="text-muted ps-4">{contract.contractAmount} kr</div>
                </li>
              )}
              {milestone && (
                <li className="mb-2">
                  <i className="feather-flag me-2 text-muted"></i>
                  {t('adminTransactionDetail.labels.milestone', { number: milestone.milestoneNumber })}
                  <div className="text-muted ps-4">{milestone.description} — {fmtKr(milestone.amount)}</div>
                </li>
              )}
              {company && (
                <li className="mb-2">
                  <i className="feather-briefcase me-2 text-muted"></i>
                  {company.companyName} (#{company.companyNumber})
                </li>
              )}
              {customer && (
                <li className="mb-2">
                  <i className="feather-user me-2 text-muted"></i>
                  {customer.name} (#{customer.customerNumber})
                  <div className="text-muted ps-4">{customer.email}</div>
                </li>
              )}
            </ul>
          </Card>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <Card title={t('adminTransactionDetail.labels.refund')}>
            <div className="small mb-3">
              <div className="d-flex justify-content-between">
                <span className="text-muted">{t('adminTransactionDetail.labels.remainingRefundable')}</span>
                <strong>{fmtKr(selectedRefundableAmountOre / 100)}</strong>
              </div>
              {(refundability?.pendingAmountOre ?? tx.pendingAmountOre ?? 0) > 0 && (
                <div className="d-flex justify-content-between text-warning mt-1">
                  <span>{t('adminTransactionDetail.labels.pendingAtAdyen')}</span>
                  <span>{fmtKr((refundability?.pendingAmountOre ?? tx.pendingAmountOre) / 100)}</span>
                </div>
              )}
            </div>

            {(refundability?.postPayout ?? tx.postPayout) && (
              <div className="alert alert-warning py-2 small">
                {t('adminTransactionDetail.info.postPayoutWarning')}
              </div>
            )}
            {refundMessage && <div className="alert alert-success py-2 small">{refundMessage}</div>}
            {refundError && <div className="alert alert-danger py-2 small">{refundError}</div>}

            {canConfigureRefund ? (
              <form onSubmit={submitRefund}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold" htmlFor="refund-reason">{t('adminTransactionDetail.labels.refundCase')}</label>
                    <select
                      id="refund-reason"
                      className="form-select"
                      value={refundReason}
                      onChange={event => {
                        const reason = event.target.value as RefundReason;
                        const treatment = sellerFeeTreatmentForReason(policyDefaults, reason);
                        const fundingSource = fundingSourceForReason(
                          policyDefaults,
                          reason,
                          treatment
                        );
                        setRefundReason(reason);
                        setSellerFeeTreatment(treatment);
                        setSellerFeeFundingSource(fundingSource);
                        const maximum = refundableForPolicy(data, reason, treatment);
                        setRefundAmount(
                          reason === 'conflict_step_1'
                            ? ''
                            : (maximum / 100).toFixed(2)
                        );
                        setRefundConfirmed(false);
                      }}
                      disabled={refundSubmitting}
                      required
                    >
                      <option value="" disabled>{t('adminTransactionDetail.options.chooseRefundCase')}</option>
                      <option value="conflict_step_1">{t('adminTransactionDetail.options.conflictStep1')}</option>
                      <option value="conflict_level_2">{t('adminTransactionDetail.options.conflictLevel2')}</option>
                      <option value="withdrawal_right">{t('adminTransactionDetail.options.withdrawalRight')}</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold" htmlFor="seller-fee-treatment">{t('adminTransactionDetail.labels.sellerFee')}</label>
                    <select
                      id="seller-fee-treatment"
                      className="form-select"
                      value={sellerFeeTreatment}
                      onChange={event => {
                        const treatment = event.target.value as 'retain' | 'refund';
                        setSellerFeeTreatment(treatment);
                        setSellerFeeFundingSource(
                          fundingSourceForReason(policyDefaults, refundReason, treatment)
                        );
                        const maximum = refundableForPolicy(
                          data,
                          refundReason,
                          treatment
                        );
                        setRefundAmount(
                          refundReason === 'conflict_step_1'
                            ? ''
                            : (maximum / 100).toFixed(2)
                        );
                        setRefundConfirmed(false);
                      }}
                      disabled={
                        refundSubmitting ||
                        !['conflict_step_1', 'conflict_level_2'].includes(refundReason)
                      }
                    >
                      <option value="retain">{t('adminTransactionDetail.options.buyerPaysFee')}</option>
                      <option value="refund">{t('adminTransactionDetail.options.refundedToBuyer')}</option>
                    </select>
                  </div>
                  {(refundReason === 'withdrawal_right' ||
                    (['conflict_step_1', 'conflict_level_2'].includes(refundReason) &&
                      sellerFeeTreatment === 'refund')) && (
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="seller-fee-funding-source">
                        {t('adminTransactionDetail.labels.sellerFeeFundedFrom')}
                      </label>
                      <select
                        id="seller-fee-funding-source"
                        className="form-select"
                        value={sellerFeeFundingSource}
                        onChange={event => {
                          setSellerFeeFundingSource(
                            event.target.value as SellerFeeFundingSource
                          );
                          setRefundConfirmed(false);
                        }}
                        disabled={refundSubmitting}
                      >
                        <option value="etappio_balance">{t('adminTransactionDetail.options.etappioBalance')}</option>
                        <option value="seller_balance">{t('adminTransactionDetail.options.sellerBalance')}</option>
                      </select>
                    </div>
                  )}
                </div>
                <label className="form-label small fw-semibold" htmlFor="refund-amount">{t('adminTransactionDetail.labels.amountKr')}</label>
                <div className="input-group">
                  <input
                    id="refund-amount"
                    className="form-control"
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={selectedRefundableAmountOre / 100}
                    value={refundAmount}
                    onChange={event => {
                      setRefundAmount(event.target.value);
                      setRefundRequestKey(null);
                      setRefundError(null);
                      setRefundConfirmed(false);
                    }}
                    disabled={
                      refundSubmitting ||
                      refundReason === 'withdrawal_right' ||
                      refundReason === 'conflict_level_2'
                    }
                    required
                  />
                  <button className="btn btn-danger" type="submit" disabled={refundSubmitting || !refundConfirmed || selectedRefundableAmountOre <= 0}>
                    {refundSubmitting ? (
                      <><span className="spinner-border spinner-border-sm me-2" />{t('adminTransactionDetail.actions.sending')}</>
                    ) : (
                      t('adminTransactionDetail.actions.initiateRefund')
                    )}
                  </button>
                </div>
                <div className="form-text">
                  {refundReason === 'withdrawal_right'
                    ? t('adminTransactionDetail.formText.withdrawalRight', { account: fundingAccountText })
                    : refundReason === 'conflict_step_1'
                      ? t('adminTransactionDetail.formText.conflictStep1', {
                          feeClause:
                            sellerFeeTreatment === 'refund'
                              ? t('adminTransactionDetail.formText.feeRefundedClause', { account: fundingAccountText })
                              : t('adminTransactionDetail.formText.conflictStep1FeeRetained')
                        })
                      : refundReason === 'conflict_level_2'
                        ? t('adminTransactionDetail.formText.conflictLevel2', {
                            feeClause:
                              sellerFeeTreatment === 'refund'
                                ? t('adminTransactionDetail.formText.feeRefundedClause', { account: fundingAccountText })
                                : ''
                          })
                        : t('adminTransactionDetail.formText.default')}
                </div>
                <div className="mt-3">
                  <label className="form-label small fw-semibold" htmlFor="refund-note">
                    {refundReason === 'conflict_step_1'
                      ? t('adminTransactionDetail.labels.partiesAgreement')
                      : t('adminTransactionDetail.labels.note')}
                  </label>
                  <textarea
                    id="refund-note"
                    className="form-control"
                    value={refundNote}
                    onChange={event => {
                      setRefundNote(event.target.value);
                      setRefundConfirmed(false);
                    }}
                    required={refundReason === 'conflict_step_1'}
                    disabled={refundSubmitting}
                    rows={2}
                  />
                </div>
                <div className="form-check mt-3">
                  <input
                    id="confirm-refund"
                    className="form-check-input"
                    type="checkbox"
                    checked={refundConfirmed}
                    onChange={event => setRefundConfirmed(event.target.checked)}
                    disabled={refundSubmitting}
                  />
                  <label className="form-check-label small" htmlFor="confirm-refund">
                    {t('adminTransactionDetail.labels.confirmRefundCheckbox')}
                  </label>
                </div>
              </form>
            ) : (
              <div className="alert alert-info py-2 small mb-0" role="status">
                {refundability?.unavailableReason ?? tx.unavailableReason ?? t('adminTransactionDetail.labels.cannotRefund')}
              </div>
            )}
          </Card>
        </div>

        <div className="col-lg-6">
          <Card title={t('adminTransactionDetail.labels.refundAttempts', { count: refunds.length })}>
            {refunds.length === 0 ? (
              <div className="text-muted small">{t('adminTransactionDetail.emptyStates.noRefunds')}</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>{t('adminTransactionDetail.columns.status')}</th>
                      <th>{t('adminTransactionDetail.columns.reference')}</th>
                      <th className="text-end">{t('adminTransactionDetail.columns.amount')}</th>
                      <th>{t('adminTransactionDetail.columns.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.map((refund: any) => {
                      const labels: Record<string, string> = {
                        pending: t('adminTransactionDetail.refundStatus.pending'),
                        confirmed: t('adminTransactionDetail.refundStatus.confirmed'),
                        failed: t('adminTransactionDetail.refundStatus.failed'),
                        reversed: t('adminTransactionDetail.refundStatus.reversed')
                      };
                      const badges: Record<string, string> = {
                        pending: 'bg-warning text-dark', confirmed: 'bg-success', failed: 'bg-danger', reversed: 'bg-secondary'
                      };
                      return (
                        <tr key={refund.id}>
                          <td><span className={`badge ${badges[refund.status] || 'bg-secondary'}`}>{labels[refund.status] || refund.status}</span></td>
                          <td className="font-monospace small" title={refund.adyenRefundPspReference || refund.etappioReference}>
                            {refund.adyenRefundPspReference || refund.etappioReference}
                            <div className="font-sans-serif text-muted">
                              {refund.refundReason === 'withdrawal_right'
                                ? t('adminTransactionDetail.refundReasonLabel.withdrawalRight')
                                : refund.refundReason === 'conflict_step_1'
                                  ? t('adminTransactionDetail.refundReasonLabel.conflictStep1')
                                  : refund.refundReason === 'conflict_level_2'
                                    ? t('adminTransactionDetail.refundReasonLabel.conflictLevel2')
                                    : t('adminTransactionDetail.refundReasonLabel.other')} ·{' '}
                              {refund.sellerFeeTreatment === 'refund'
                                ? t('adminTransactionDetail.sellerFeeStatus.refunded')
                                : t('adminTransactionDetail.sellerFeeStatus.retained')}
                              {refund.sellerFeeTreatment === 'refund' && (
                                <>
                                  {' · '}
                                  {refund.sellerFeeFundingSource === 'seller_balance'
                                    ? t('adminTransactionDetail.sellerFeeFunding.bySeller')
                                    : t('adminTransactionDetail.sellerFeeFunding.byEtappio')}
                                </>
                              )}
                            </div>
                            {refund.resolutionNote && (
                              <div className="font-sans-serif text-muted">
                                {refund.resolutionNote}
                              </div>
                            )}
                            {refund.failureMessage && <div className="text-danger font-sans-serif">{refund.failureMessage}</div>}
                          </td>
                          <td className="text-end">{fmtKr(refund.amountOre / 100)}</td>
                          <td>{new Date(refund.confirmedAt || refund.failedAt || refund.reversedAt || refund.updatedAt || refund.createdAt).toLocaleString('sv-SE')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Card className="mb-4" title={t('adminTransactionDetail.labels.etappioLedger', { count: etappioEntries.length })}>
        <div className="alert alert-info small mb-3">
          {t('adminTransactionDetail.info.etappioLedgerNote')}
        </div>
        {etappioCostSek > 0 && (
          <div className="alert alert-light border small mb-3">
            <strong>{t('adminTransactionDetail.labels.internalCalcTitle')}</strong> {t('adminTransactionDetail.info.internalCalcNote', { cost: fmtKr(etappioCostSek) })}
          </div>
        )}
        <AccountingEntriesTable entries={etappioEntries} />
      </Card>

      <Card title={t('adminTransactionDetail.labels.sellerLedger', { count: sellerEntries.length })}>
        <div className="alert alert-light border small mb-3">
          {t('adminTransactionDetail.info.sellerLedgerNote')}
        </div>
        <AccountingEntriesTable entries={sellerEntries} />
      </Card>
    </DashboardLayout>
  );
}
